<template>
  <div class="h-full min-h-0 flex flex-col pt-4 px-8">
    <div class="flex flex-1 min-h-0 items-stretch">
      <div
        v-show="activeTab == 'MESSAGE'"
        class="flex-col pr-5 min-h-0 overflow-auto"
        style="width: 808px"
      >
        <div class="flex-1 flex flex-col gap-4 pb-4">
          <BaseInput
            v-model="form.title"
            style="width: 41ch"
            label="Rubrik"
            type="text"
            required
          />

          <BaseToggle
            v-model="form.isBanner"
            label="Banner"
            class="flex-row-reverse self-start"
          />

          <div
            v-if="form.isBanner"
            class="flex flex-col gap-4 pl-4 border-l border-gray-300 -mt-2"
          >
            <BaseInput
              v-model="form.bannerMessage"
              label="Bannermeddelande"
              required
            />

            <div>
              <label class="mb-1 text-sm font-semibold tracking-wide">
                Färg banner <span class="text-coral-500">*</span>
              </label>
              <div class="flex gap-4">
                <div
                  v-for="bannerLevel in Object.values(BannerLevel).reverse()"
                  :key="bannerLevel"
                  class="h-10 w-10 cursor-pointer border-2 border-transparent"
                  :class="bannerLevel === form.bannerLevel && 'border-blue-500'"
                  :style="{ backgroundColor: getBannerColor(bannerLevel) }"
                  @click="form.bannerLevel = bannerLevel"
                />
              </div>
            </div>

            <BaseToggle
              v-model="form.isBannerDismissable"
              stacked
              label="Stänga banner"
              class="text-sm font-semibold tracking-wide"
            />
          </div>

          <div>
            <label
              class="flex items-center font-semibold text-gray-900 cursor-pointer select-none"
              @click="showShortMessage = !showShortMessage"
            >
              <svg
                fill="none"
                viewBox="0 0 24 24"
                height="20"
                width="20"
                xmlns="http://www.w3.org/2000/svg"
                class="fill-current transition-transform duration-200"
                :class="showShortMessage && 'transform rotate-90'"
              >
                <path
                  xmlns="http://www.w3.org/2000/svg"
                  d="M9.29289 18.7071C8.90237 18.3166 8.90237 17.6834 9.29289 17.2929L14.5858 12L9.29289 6.70711C8.90237 6.31658 8.90237 5.68342 9.29289 5.29289C9.68342 4.90237 10.3166 4.90237 10.7071 5.29289L16.7071 11.2929C17.0976 11.6834 17.0976 12.3166 16.7071 12.7071L10.7071 18.7071C10.3166 19.0976 9.68342 19.0976 9.29289 18.7071Z"
                />
              </svg>
              Kort meddelande
            </label>
            <VueEditor
              v-if="showShortMessage"
              v-model="form.shortMessage"
              :editor-toolbar="editorToolbar"
            />
          </div>
          <label class="font-semibold text-gray-900">
            Långt meddelande
          </label>
          <VueEditor
            v-model="form.longMessage"
            :editor-toolbar="editorToolbar"
          />
        </div>
      </div>
      <SelectRecipients
        v-show="activeTab == 'RECIPIENTS'"
        v-model="recipients"
        class="mr-5 flex-1"
      />
      <div
        class="flex flex-col items-center border-l border-gray-300 pl-5 justify-between"
        :class="
          activeTab == 'MESSAGE'
            ? 'flex-1 max-w-4xl'
            : 'flex-none w-full max-w-lg'
        "
      >
        <MessagePreview
          v-if="activeTab == 'MESSAGE'"
          :short-message="form.shortMessage"
          :long-message="form.longMessage"
          :title="form.title"
          class="mt-8"
        />
        <div v-else class="px-24 min-h-0 flex flex-col">
          <h2 class="mb-4 text-lg text-center">
            Skickar till
            {{ filtersUsed.includes('ALL') ? 'alla' : selectedSaloons.length }}
            {{ selectedSaloons.length == 1 ? 'salong' : 'salonger' }}
          </h2>
          <ul class="overflow-auto min-h-0 flex-1">
            <li
              v-for="saloon of selectedSaloons"
              :key="saloon.id"
              class="max-w-full flex flex-wrap"
            >
              <p class="font-semibold truncate mr-1">
                {{ getCompanyDisplayName(saloon.company) }}
              </p>
              <p class="truncate">- {{ saloon.name }}</p>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="flex flex-row-reverse justify-between items-end border-t pt-4">
      <div v-if="activeTab == 'RECIPIENTS'">
        <p class="mb-2 text-coral-500">{{ errorText }}</p>
        <button
          class="btn btn-primary mx-auto "
          :disabled="
            selectedSaloons.length == 0 && !filtersUsed.includes('ALL')
          "
          @click="sendMessage"
        >
          Skicka till
          {{ filtersUsed.includes('ALL') ? 'alla' : selectedSaloons.length }}
          {{ selectedSaloons.length == 1 ? 'salong' : 'salonger' }}
        </button>
      </div>
      <button
        class="btn btn-secondary flex items-center justify-center"
        :disabled="isFormInvalid"
        @click="
          activeTab = activeTab == 'RECIPIENTS' ? 'MESSAGE' : 'RECIPIENTS'
        "
      >
        <span v-if="activeTab == 'MESSAGE'">
          Välj mottagare
        </span>
        <svg
          class="fill-current"
          :class="
            activeTab == 'MESSAGE' ? '-mr-1 transform rotate-180' : '-ml-1'
          "
          viewBox="0 0 24 24"
          height="20"
          width="20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14.7071 5.29289C15.0976 5.68342 15.0976 6.31658 14.7071 6.70711L9.41421 12L14.7071 17.2929C15.0976 17.6834 15.0976 18.3166 14.7071 18.7071C14.3166 19.0976 13.6834 19.0976 13.2929 18.7071L7.29289 12.7071C6.90237 12.3166 6.90237 11.6834 7.29289 11.2929L13.2929 5.29289C13.6834 4.90237 14.3166 4.90237 14.7071 5.29289Z"
          />
        </svg>
        <span v-if="activeTab == 'RECIPIENTS'">
          Redigera meddelande
        </span>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { VueEditor, Quill } from 'vue2-editor'
import BaseToggle from '@/components/BaseToggle.vue'
import MessagePreview from './MessagePreview'
import SelectRecipients from './SelectRecipients'
import gql from 'graphql-tag'
import { BannerLevel, getBannerColor } from './banner.helpers'

const AlignStyle = Quill.import('attributors/style/align')
Quill.register(AlignStyle, true)
const BackgroundStyle = Quill.import('attributors/style/background')
Quill.register(BackgroundStyle, true)
const ColorStyle = Quill.import('attributors/style/color')
Quill.register(ColorStyle, true)
const FontStyle = Quill.import('attributors/style/font')
Quill.register(FontStyle, true)
const SizeStyle = Quill.import('attributors/style/size')
SizeStyle.whitelist = ['1rem', '0.875rem', '1em', '1.125rem', '1.25rem']
Quill.register(SizeStyle, true)

const colorSwatches = '#000000,#e60000,#ffff00,#008a00,#0066cc,#EB6F93,#227D85,#ffffff,#facccc,#ffffcc,#cce8cc,#cce0f5,#FDF1F5,#EAF3F3,#bbbbbb,#f06666,#ffff66,#66b966,#66a3e0,#F5BDCD,#9ac3c7,#888888,#a10000,#b2b200,#006100,#0047b2,#EE89A6,#4a949b,#444444,#5c0000,#666600,#003700,#002966,#C15B79,#1c676d'.split(
  ','
)

export default {
  name: 'Create',
  components: {
    BaseToggle,
    MessagePreview,
    VueEditor,
    SelectRecipients,
  },
  data() {
    return {
      form: {
        bannerLevel: null,
        bannerMessage: '',
        isBanner: false,
        isBannerDismissable: false,
        title: '',
        shortMessage: '',
        longMessage: '',
      },
      selectedSaloons: [],
      filtersUsed: [],
      activeTab: 'MESSAGE',
      showShortMessage: true,
      errorText: '',
      editorToolbar: [
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        [{ size: ['1rem', '0.875rem', '1em', '1.125rem', '1.25rem'] }], // double '1rem' / '1em' in order to get 'Normal' as default
        ['bold', 'italic', 'underline', 'strike'], // toggle buttons
        [
          { align: '' },
          { align: 'center' },
          { align: 'right' },
          { align: 'justify' },
        ],
        // ['blockquote', 'code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
        // [{ indent: '-1' }, { indent: '+1' }], // outdent, indent // disabled because its implemented using classes instead of inline style
        [{ color: colorSwatches }, { background: colorSwatches }],
        ['link'],
        ['clean'], // remove formatting button
      ],
      BannerLevel,
    }
  },
  computed: {
    isFormInvalid() {
      const { form } = this

      if (!form.title) {
        return true
      }
      if (!form.shortMessage && !form.longMessage) {
        return true
      }
      if (form.isBanner && (!form.bannerMessage || !form.bannerLevel)) {
        return true
      }

      return false
    },
    recipients: {
      get() {
        return {
          selectedSaloons: this.selectedSaloons,
          filtersUsed: this.filtersUsed,
        }
      },
      set(data) {
        this.selectedSaloons = (data.selectedSaloons || []).sort((a, b) =>
          this.getCompanyDisplayName(a.company).localeCompare(
            this.getCompanyDisplayName(b.company)
          )
        )
        this.filtersUsed = data.filtersUsed
      },
    },
  },
  methods: {
    getCompanyDisplayName(company) {
      return (company.displayname || company.name).trim()
    },
    setErrorText(text) {
      this.errorText = text
      setTimeout(() => (this.errorText = ''), 5000)
    },
    async sendMessage() {
      if (
        !this.form.title ||
        (!this.form.shortMessage && !this.form.longMessage)
      ) {
        return this.setErrorText('Meddelandet saknar rubrik eller text')
      }

      if (
        this.selectedSaloons.length == 0 &&
        !this.filtersUsed.includes('ALL')
      ) {
        return this.setErrorText('Ingen mottagare vald')
      }

      const confirmation = await this.$swal({
        title: 'Bekräfta skicka meddelande',
      })

      if (!confirmation.isConfirmed) return

      try {
        const {
          data: {
            createVoadyMessage: { success },
          },
        } = await this.$apollo.mutate({
          mutation: gql`
            mutation createVoadyMessage($data: CreateVoadyMessageInput!) {
              createVoadyMessage(data: $data) {
                success
              }
            }
          `,
          client: 'voadyApi',
          variables: {
            data: {
              ...(this.form.isBanner && {
                isBanner: true,
                bannerData: {
                  isBannerDismissable: this.form.isBannerDismissable,
                  bannerLevel: this.form.bannerLevel,
                  bannerMessage: this.form.bannerMessage,
                },
              }),
              title: this.form.title,
              shortMessage: this.form.shortMessage,
              longMessage: this.form.longMessage,
              saloonIds: this.selectedSaloons.map(saloon => saloon.id),
              filtersUsed: this.filtersUsed,
              global: this.filtersUsed.includes('ALL'),
            },
          },
        })

        if (success) {
          localStorage.setItem('newMessage', 'null')
          this.$router.push({ name: 'messages.archive' })
        }
      } catch (error) {
        this.setErrorText(error.message)
        console.log(error)
      }
    },
    getBannerColor,
  },
  watch: {
    form: {
      handler(value) {
        localStorage.setItem('newMessage', JSON.stringify(value))
      },
      deep: true,
    },
  },
  async mounted() {
    const storedMessage = JSON.parse(await localStorage.getItem('newMessage'))

    if (storedMessage) {
      Object.keys(storedMessage).forEach(
        key =>
          this.form.hasOwnProperty(key) && (this.form[key] = storedMessage[key])
      )
    }
  },
}
</script>

<style>
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value='1rem']::before {
  content: 'Font size';
  @apply text-base !important;
}
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value='0.875rem']::before {
  content: 'Small';
  @apply text-sm !important;
}
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value='1em']::before {
  content: 'Normal';
  @apply text-base !important;
}
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value='1.125rem']::before {
  content: 'Large';
  @apply text-lg !important;
}
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value='1.25rem']::before {
  content: 'XL';
  @apply text-xl !important;
}
.ql-picker-label[data-value='1rem']::before {
  content: 'Font size' !important;
}
.ql-picker-label[data-value='0.875rem']::before {
  content: 'Small' !important;
}
.ql-picker-label[data-value='1em']::before {
  content: 'Normal' !important;
}
.ql-picker-label[data-value='1.125rem']::before {
  content: 'Large' !important;
}
.ql-picker-label[data-value='1.25rem']::before {
  content: 'XL' !important;
}

#quill-container h1,
#quill-container h2,
#quill-container h3,
#quill-container h4,
#quill-container h5,
#quill-container h6 {
  font-size: 1rem;
  font-weight: 400;
  margin-top: 0;
  margin-bottom: 0.5rem;
}
#quill-container {
  color: #1a202c;
  font-family: proxima-nova, sans-serif;
}
.quill-form .form-control {
  color: #1a202c;
}
</style>
